import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
/** Courier accounts and shipments (S-01, S-02; docs/specs/shipping.md). */
export const COURIER_PROVIDERS = ["steadfast", "pathao", "redx"];
export const courierProviderSchema = z.enum(COURIER_PROVIDERS);
/**
 * Booking state of a shipment: `queued` (the booking job will call the courier), `booked` (the courier has the
 * parcel), `failed` (every attempt failed; retry or pick another courier), `cancelled`.
 */
export const SHIPMENT_STATUSES = ["queued", "booked", "failed", "cancelled"];
export const shipmentStatusSchema = z.enum(SHIPMENT_STATUSES);
/** The courier's tracking status mapped to ours (each adapter maps its own; tracking updates come with B-38). */
export const COURIER_STATUSES = [
    "pending",
    "in_transit",
    "on_hold",
    "delivered",
    "partially_delivered",
    "returned",
    "cancelled",
    "unknown",
];
export const courierStatusSchema = z.enum(COURIER_STATUSES);
/** Courier statuses that end tracking: no later update changes them (B-38). */
export const FINAL_COURIER_STATUSES = ["delivered", "partially_delivered", "returned", "cancelled"];
/** S-06: the courier statuses a customer can be texted about (each switched on in the shipping settings). */
export const SMS_COURIER_STATUSES = ["in_transit", "on_hold", "delivered", "returned"];
export const smsCourierStatusSchema = z.enum(SMS_COURIER_STATUSES);
/** S-05: a returned parcel waits to be scanned in (`pending`); scanning puts its goods back in stock (`received`). */
export const RTO_STATUSES = ["pending", "received"];
export const rtoStatusSchema = z.enum(RTO_STATUSES);
/** The orders a shipment can be booked for: confirmed or packed, with a delivery address. */
export const SHIPPABLE_ORDER_STATUSES = ["confirmed", "packed"];
const secret = z.string().trim().min(1).max(200);
/** Per provider. Never returned: responses carry `credentialsHint` only. */
export const courierCredentialsSchema = z.discriminatedUnion("provider", [
    z.object({ provider: z.literal("steadfast"), apiKey: secret, secretKey: secret }).strict(),
    z
        .object({
        provider: z.literal("pathao"),
        clientId: secret,
        clientSecret: secret,
        username: secret,
        password: secret,
        storeId: z.coerce.number().int().positive(),
    })
        .strict(),
    z
        .object({
        provider: z.literal("redx"),
        apiKey: secret,
        storeId: z.coerce.number().int().positive(),
    })
        .strict(),
]);
export const courierAccountSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    provider: courierProviderSchema,
    /** The API key's last four characters, so the account can be recognised (`••••3f9a`). */
    credentialsHint: z.string(),
    /** S-02: book with this account as soon as an order is confirmed (the default account only). */
    autoBookOnConfirm: z.boolean(),
    isDefault: z.boolean(),
    status: z.enum(["active", "archived"]),
    /** B-38: a webhook URL was created (it is shown once; creating a new one replaces it). */
    webhookConfigured: z.boolean(),
    version: z.int(),
    createdAt: z.iso.datetime(),
});
export const courierAccountListSchema = z.object({ data: z.array(courierAccountSchema) });
export const createCourierAccountRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    credentials: courierCredentialsSchema,
    autoBookOnConfirm: z.boolean().default(false),
    isDefault: z.boolean().default(false),
})
    .strict();
export const updateCourierAccountRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(100).optional(),
    /** Replaces the stored credentials (same provider). */
    credentials: courierCredentialsSchema.optional(),
    autoBookOnConfirm: z.boolean().optional(),
    isDefault: z.boolean().optional(),
    status: z.enum(["active", "archived"]).optional(),
    version: z.int().min(1),
})
    .strict();
export const shipmentSchema = z.object({
    id: z.uuid(),
    orderId: z.uuid(),
    orderNumber: z.string(),
    locationId: z.uuid(),
    courierAccountId: z.uuid(),
    provider: courierProviderSchema,
    status: shipmentStatusSchema,
    /** What the courier collects from the customer. */
    codPaisa: paisa(),
    recipientName: z.string(),
    recipientPhone: z.string(),
    recipientAddress: z.string(),
    note: z.string(),
    /** Our reference sent to the courier (order number, plus `-2`, `-3` … for later bookings of the same order). */
    invoice: z.string(),
    consignmentId: z.string().nullable(),
    trackingCode: z.string().nullable(),
    trackingUrl: z.string().nullable(),
    labelUrl: z.string().nullable(),
    courierStatus: courierStatusSchema.nullable(),
    /** The courier's own status word, as received. */
    courierStatusRaw: z.string().nullable(),
    /** When the courier status last changed (the courier's time where it gave one). */
    courierStatusAt: z.iso.datetime().nullable(),
    /** S-05: null until the courier reports the parcel returned. */
    rtoStatus: rtoStatusSchema.nullable(),
    rtoReceivedAt: z.iso.datetime().nullable(),
    /** Why the last booking attempt failed (cleared once booked). */
    lastError: z.string().nullable(),
    attempts: z.int(),
    bookedAt: z.iso.datetime().nullable(),
    cancelledAt: z.iso.datetime().nullable(),
    version: z.int(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const shipmentEventSchema = z.object({
    id: z.uuid(),
    kind: z.enum([
        "requested",
        "attempt_failed",
        "booked",
        "failed",
        "retried",
        "cancelled",
        "status",
        "rto_received",
    ]),
    message: z.string(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const shipmentDetailSchema = shipmentSchema.extend({ events: z.array(shipmentEventSchema) });
export const shipmentListQuerySchema = z.object({
    orderId: z.uuid().optional(),
    status: shipmentStatusSchema.optional(),
    courierAccountId: z.uuid().optional(),
    rtoStatus: rtoStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const shipmentListSchema = z.object({
    data: z.array(shipmentSchema),
    nextCursor: z.uuid().nullable(),
});
/** `POST /v1/shipments`: book one order. The courier is called by a job; the shipment starts `queued`. */
export const bookShipmentRequestSchema = z
    .object({
    orderId: z.uuid(),
    /** Left out: the default courier account. */
    courierAccountId: z.uuid().optional(),
    /** Left out: the order's total less its advance (0 when the order is paid). */
    codPaisa: paisa().min(0).optional(),
    note: z.string().trim().max(250).default(""),
})
    .strict();
export const MAX_BULK_SHIPMENTS = 100;
export const bulkBookShipmentsRequestSchema = z
    .object({
    orderIds: z.array(z.uuid()).min(1).max(MAX_BULK_SHIPMENTS),
    courierAccountId: z.uuid().optional(),
})
    .strict();
/** Per order: the queued shipment, or why it was not queued (an error code). */
export const bulkBookShipmentsResultSchema = z.object({
    results: z.array(z.object({ orderId: z.uuid(), shipmentId: z.uuid().nullable(), error: z.string().nullable() })),
});
export const cancelShipmentRequestSchema = z
    .object({ reason: z.string().trim().min(1).max(250), version: z.int().min(1) })
    .strict();
export const retryShipmentRequestSchema = z.object({ version: z.int().min(1) }).strict();
/** B-38: the account's webhook path (`/v1/webhooks/couriers/{tenantId}/{accountId}/{token}`), shown once. */
export const courierWebhookTokenSchema = z.object({ webhookPath: z.string(), account: courierAccountSchema });
export const courierWebhookParamsSchema = z.object({
    tenantId: z.uuid(),
    accountId: z.uuid(),
    token: z.string().min(16).max(100),
});
/** What the courier sends; each adapter reads its own shape (unknown shapes are accepted and ignored). */
export const courierWebhookBodySchema = z.unknown();
export const courierWebhookResultSchema = z.object({ received: z.int() });
/** S-05: a returned parcel scanned in by its tracking code, consignment ID or invoice. */
export const receiveRtoRequestSchema = z
    .object({ code: z.string().trim().min(1).max(100), note: z.string().trim().max(250).default("") })
    .strict();
/** S-06 settings; defaults (no SMS) until first saved (`version` 0). */
export const shippingSettingsSchema = z.object({
    smsStatuses: z.array(smsCourierStatusSchema),
    version: z.int(),
});
export const updateShippingSettingsRequestSchema = z
    .object({
    smsStatuses: z.array(smsCourierStatusSchema).max(SMS_COURIER_STATUSES.length),
    version: z.int().min(0),
})
    .strict();
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "shipping" };
const manage = { permission: "couriers.manage" };
const view = { permission: "shipments.view" };
const book = { permission: "shipments.book" };
export const listCourierAccounts = defineEndpoint({
    ...tag,
    name: "listCourierAccounts",
    method: "GET",
    path: "/v1/courier-accounts",
    access: view,
    summary: "The tenant's courier merchant accounts (credentials never returned)",
    status: 200,
    response: courierAccountListSchema,
});
export const createCourierAccount = defineEndpoint({
    ...tag,
    name: "createCourierAccount",
    method: "POST",
    path: "/v1/courier-accounts",
    access: manage,
    summary: "Connect a courier merchant account (credentials stored encrypted)",
    body: createCourierAccountRequestSchema,
    status: 201,
    response: courierAccountSchema,
    errors: ["COURIER_NOT_SUPPORTED"],
});
export const updateCourierAccount = defineEndpoint({
    ...tag,
    name: "updateCourierAccount",
    method: "PATCH",
    path: "/v1/courier-accounts/{id}",
    access: manage,
    summary: "Rename, replace credentials, set default or auto-booking, or archive a courier account",
    params: idParamsSchema,
    body: updateCourierAccountRequestSchema,
    status: 200,
    response: courierAccountSchema,
    errors: ["CONFLICT", "COURIER_NOT_SUPPORTED"],
});
export const listShipments = defineEndpoint({
    ...tag,
    name: "listShipments",
    method: "GET",
    path: "/v1/shipments",
    access: view,
    summary: "Shipments, newest first (filter by order, status, courier account)",
    query: shipmentListQuerySchema,
    status: 200,
    response: shipmentListSchema,
});
export const getShipment = defineEndpoint({
    ...tag,
    name: "getShipment",
    method: "GET",
    path: "/v1/shipments/{id}",
    access: view,
    summary: "One shipment with its booking history",
    params: idParamsSchema,
    status: 200,
    response: shipmentDetailSchema,
});
export const bookShipment = defineEndpoint({
    ...tag,
    name: "bookShipment",
    method: "POST",
    path: "/v1/shipments",
    access: book,
    summary: "Book a courier for a confirmed or packed order (the courier is called by a job)",
    body: bookShipmentRequestSchema,
    idempotent: true,
    status: 201,
    response: shipmentDetailSchema,
    errors: ["ORDER_NOT_SHIPPABLE", "SHIPMENT_EXISTS", "NO_COURIER_ACCOUNT", "COURIER_ACCOUNT_ARCHIVED"],
});
export const bulkBookShipments = defineEndpoint({
    ...tag,
    name: "bulkBookShipments",
    method: "POST",
    path: "/v1/shipments/bulk",
    access: book,
    summary: "Book a courier for up to 100 orders; each order's outcome is returned",
    body: bulkBookShipmentsRequestSchema,
    idempotent: true,
    status: 200,
    response: bulkBookShipmentsResultSchema,
    errors: ["NO_COURIER_ACCOUNT", "COURIER_ACCOUNT_ARCHIVED"],
});
export const retryShipment = defineEndpoint({
    ...tag,
    name: "retryShipment",
    method: "POST",
    path: "/v1/shipments/{id}/retry",
    access: book,
    summary: "Queue a failed booking again",
    params: idParamsSchema,
    body: retryShipmentRequestSchema,
    status: 200,
    response: shipmentDetailSchema,
    errors: ["INVALID_TRANSITION", "CONFLICT", "SHIPMENT_EXISTS"],
});
export const cancelShipment = defineEndpoint({
    ...tag,
    name: "cancelShipment",
    method: "POST",
    path: "/v1/shipments/{id}/cancel",
    access: book,
    summary: "Cancel a queued or failed booking (a booked parcel only where the courier allows it)",
    params: idParamsSchema,
    body: cancelShipmentRequestSchema,
    status: 200,
    response: shipmentDetailSchema,
    errors: ["INVALID_TRANSITION", "CONFLICT", "COURIER_CANNOT_CANCEL"],
});
export const createCourierWebhookToken = defineEndpoint({
    ...tag,
    name: "createCourierWebhookToken",
    method: "POST",
    path: "/v1/courier-accounts/{id}/webhook-token",
    access: manage,
    summary: "Create the account's webhook URL (replaces an earlier one); shown once",
    params: idParamsSchema,
    status: 200,
    response: courierWebhookTokenSchema,
});
export const receiveCourierWebhook = defineEndpoint({
    ...tag,
    name: "receiveCourierWebhook",
    method: "POST",
    path: "/v1/webhooks/couriers/{tenantId}/{accountId}/{token}",
    access: { public: true },
    summary: "A courier's status update (the token in the path identifies the account); duplicates are harmless",
    params: courierWebhookParamsSchema,
    body: courierWebhookBodySchema,
    status: 200,
    response: courierWebhookResultSchema,
});
export const receiveRto = defineEndpoint({
    ...tag,
    name: "receiveRto",
    method: "POST",
    path: "/v1/shipments/rto-receipts",
    access: { permission: "shipments.receive_rto" },
    summary: "Scan in a returned parcel: the order becomes returned and its goods go back into stock",
    body: receiveRtoRequestSchema,
    idempotent: true,
    status: 200,
    response: shipmentDetailSchema,
    errors: ["RTO_ALREADY_RECEIVED", "SHIPMENT_NOT_RETURNABLE"],
});
export const getShippingSettings = defineEndpoint({
    ...tag,
    name: "getShippingSettings",
    method: "GET",
    path: "/v1/shipping-settings",
    access: view,
    summary: "Which courier statuses text the customer",
    status: 200,
    response: shippingSettingsSchema,
});
export const updateShippingSettings = defineEndpoint({
    ...tag,
    name: "updateShippingSettings",
    method: "PATCH",
    path: "/v1/shipping-settings",
    access: manage,
    summary: "Choose which courier statuses text the customer",
    body: updateShippingSettingsRequestSchema,
    status: 200,
    response: shippingSettingsSchema,
    errors: ["CONFLICT"],
});
//# sourceMappingURL=couriers.js.map